/**
 * Offline-first upload queue (ADR-012: "multi-file, resumable, queueable
 * offline, per-file visible state — nothing silently fails").
 *
 * The outbox discipline, applied to blobs: the file is PERSISTED to the
 * "uploads" object store — transaction complete — BEFORE any network attempt,
 * so a tunnel, a dead venue basement, or a force-quit between pick and POST
 * loses nothing; the queue replays on next launch. Same reasoning as
 * src/outbox/outbox.ts (P2.md DoD), same versionless-open pattern as
 * src/pass/store.ts: db/idb.ts owns DB_NAME + DB_VERSION and creates this
 * store additively on upgrade — two modules racing different versions is how
 * VersionError boot failures happen.
 *
 * State machine, all transitions surfaced via onChange (never silent):
 *   pending → uploading(progress 0–1) → stored (record deleted; the synced
 *   files-collection row takes over in FilesList) → or failed(error, attempts).
 *
 * Failure honesty (the flush.ts contract, ported):
 *  - transient (network / 5xx): failed + ONE exponential-backoff timer
 *    (5s → 10s → … capped 60s) auto-retries — "queued" must mean "will retry";
 *  - rejected (4xx, e.g. 413): failed with the reason, NO auto-retry — looping
 *    on a request the server just refused is noise; the row's Retry button
 *    owns another attempt;
 *  - 401: the queue PAUSES (login owns recovery — see src/outbox/flush.ts);
 *    the next trigger (focus after sign-in) resumes it.
 */

import { DB_NAME } from "../db/idb.ts";

export const UPLOADS_STORE = "uploads";

/** Bump ONLY with a reader for every previous version (records outlive builds
 * — the src/outbox/outbox.ts record_v rule). */
export const UPLOAD_RECORD_VERSION = 1;

/** Backoff cap: never sleep longer than a minute while files are queued
 * (mirrors flush.ts RETRY_MAX_MS). */
export const UPLOAD_RETRY_MAX_MS = 60_000;
const UPLOAD_RETRY_BASE_MS = 5_000;

/** The persisted record — blob + meta, durable before any network attempt. */
export interface UploadRecord {
  record_v: number;
  upload_id: string;
  gig_id: string;
  /** Original file name (the server re-sanitizes for its storage key). */
  name: string;
  mime: string;
  size: number;
  blob: Blob;
  /** Epoch ms of the enqueue on this device. */
  queued_at: number;
}

/** Runtime state — NOT persisted: after a relaunch every surviving record is
 * honestly "pending" again (attempt counts describe THIS session's tries). */
export type UploadState =
  | { status: "pending" }
  | { status: "uploading"; progress: number }
  | { status: "failed"; error: string; attempts: number; retryable: boolean };

export interface UploadView {
  upload_id: string;
  gig_id: string;
  name: string;
  mime: string;
  size: number;
  /** The local blob — FilesList paints image thumbnails from it INSTANTLY. */
  blob: Blob;
  state: UploadState;
}

/** Persistence surface — IdbUploadStore in the app, MemoryUploadStore in tests
 * (vitest runs in Node, no IndexedDB — the src/db/idb.ts LocalStore pattern). */
export interface UploadStore {
  uploadsPut(record: UploadRecord): Promise<void>;
  uploadsList(): Promise<UploadRecord[]>;
  uploadsDelete(uploadId: string): Promise<void>;
}

// ---------------------------------------------------------------- transport

export type UploadSendOutcome =
  | { kind: "stored"; file_id: string; item_id: string }
  | { kind: "unauthenticated" } // 401 — login owns recovery, queue pauses
  | { kind: "rejected"; detail: string } // 4xx — this file won't succeed unchanged
  | { kind: "error"; detail: string }; // network / 5xx — transient, backoff retries

export type SendUploadFn = (
  record: UploadRecord,
  onProgress: (fraction: number) => void,
) => Promise<UploadSendOutcome>;

/**
 * The production transport: XMLHttpRequest (NOT fetch) because upload
 * progress events only exist on XHR — the per-file percentage in FilesList
 * must be real, never spinner theater (ADR-014 half 2).
 */
export function sendUploadXhr(record: UploadRecord, onProgress: (fraction: number) => void): Promise<UploadSendOutcome> {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `/api/gigs/${encodeURIComponent(record.gig_id)}/files`);
    xhr.responseType = "text";
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && e.total > 0) onProgress(Math.min(e.loaded / e.total, 1));
    };
    xhr.onerror = () => resolve({ kind: "error", detail: "network error" });
    xhr.onabort = () => resolve({ kind: "error", detail: "upload aborted" });
    xhr.onload = () => {
      if (xhr.status === 401) {
        resolve({ kind: "unauthenticated" });
        return;
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const body = JSON.parse(xhr.responseText) as { file_id?: unknown; item_id?: unknown };
          if (typeof body.file_id === "string" && typeof body.item_id === "string") {
            resolve({ kind: "stored", file_id: body.file_id, item_id: body.item_id });
            return;
          }
        } catch {
          // fall through — a 2xx with an unreadable body is NOT a success we can prove
        }
        resolve({ kind: "error", detail: "unexpected upload response shape" });
        return;
      }
      if (xhr.status >= 400 && xhr.status < 500) {
        resolve({ kind: "rejected", detail: `HTTP ${xhr.status}` });
        return;
      }
      resolve({ kind: "error", detail: `HTTP ${xhr.status}` });
    };
    const form = new FormData();
    form.append("file", record.blob, record.name);
    xhr.send(form);
  });
}

// -------------------------------------------------------------------- queue

export interface UploadQueueDeps {
  store: UploadStore;
  send?: SendUploadFn;
  /** Backoff base (ms); tests shrink it; 0 disables the timer (flush.ts convention). */
  retryBaseMs?: number;
  /** A file landed server-side: its files-collection row exists — the sync
   * pull will surface it; the App kicks its SyncEngine here. */
  onStored?(info: { upload_id: string; gig_id: string; file_id: string; item_id: string }): void;
  now?(): number;
}

export class UploadQueue {
  private readonly store: UploadStore;
  private readonly send: SendUploadFn;
  private readonly deps: UploadQueueDeps;
  private readonly retryBaseMs: number;
  private readonly now: () => number;

  /** Enqueue-ordered records mirrored in memory (source of truth = the store). */
  private records: UploadRecord[] = [];
  private states = new Map<string, UploadState>();
  /** This-session attempt counts (survive the failed→pending flip on resume). */
  private attempts = new Map<string, number>();
  private listeners = new Set<() => void>();

  private inFlight: Promise<void> | null = null;
  private runAgain = false;
  private pausedFor401 = false;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private retryAttempt = 0;
  private disposed = false;

  constructor(deps: UploadQueueDeps) {
    this.deps = deps;
    this.store = deps.store;
    this.send = deps.send ?? sendUploadXhr;
    this.retryBaseMs = deps.retryBaseMs ?? UPLOAD_RETRY_BASE_MS;
    this.now = deps.now ?? Date.now;
  }

  /** Load survivors of a previous session (crash / offline quit) as pending. */
  async init(): Promise<void> {
    this.records = await this.store.uploadsList();
    for (const r of this.records) {
      if (!this.states.has(r.upload_id)) this.states.set(r.upload_id, { status: "pending" });
    }
    this.emit();
  }

  dispose(): void {
    this.disposed = true;
    this.clearRetry();
    this.listeners.clear();
  }

  /** True while a 401 holds the queue (records stay durable; login resumes). */
  get paused(): boolean {
    return this.pausedFor401;
  }

  onChange(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  views(): UploadView[] {
    return this.records.map((r) => ({
      upload_id: r.upload_id,
      gig_id: r.gig_id,
      name: r.name,
      mime: r.mime,
      size: r.size,
      blob: r.blob,
      state: this.states.get(r.upload_id) ?? { status: "pending" },
    }));
  }

  /**
   * Persist FIRST (transaction complete), show and send second — the
   * persist-before-network guarantee this queue exists for. Never throws for
   * network reasons; a storage failure DOES throw (the caller must not claim
   * the file is queued when it is not).
   */
  async enqueue(gigId: string, file: File): Promise<string> {
    const record: UploadRecord = {
      record_v: UPLOAD_RECORD_VERSION,
      upload_id: crypto.randomUUID(),
      gig_id: gigId,
      name: file.name || "upload",
      mime: file.type || "application/octet-stream",
      size: file.size,
      blob: file,
      queued_at: this.now(),
    };
    await this.store.uploadsPut(record); // durability point
    this.records.push(record);
    this.states.set(record.upload_id, { status: "pending" });
    this.emit();
    void this.kick();
    return record.upload_id;
  }

  /** Re-attempt one failed upload NOW (the row's Retry button). */
  retry(uploadId: string): void {
    const state = this.states.get(uploadId);
    if (state === undefined || state.status !== "failed") return;
    this.states.set(uploadId, { status: "pending" });
    this.emit();
    void this.kick();
  }

  /** A fresh window opened (online/focus/backoff timer): transient failures
   * become pending again and the queue flushes. Rejected (4xx) files stay
   * failed — only their Retry button re-arms them. */
  resume(): Promise<void> {
    let flipped = false;
    for (const [id, state] of this.states) {
      if (state.status === "failed" && state.retryable) {
        this.states.set(id, { status: "pending" });
        flipped = true;
      }
    }
    if (flipped) this.emit();
    return this.kick();
  }

  /** Fire-and-forget flush — a flush uploads PENDING records only. */
  kick(): Promise<void> {
    if (this.inFlight !== null) {
      this.runAgain = true;
      return this.inFlight;
    }
    this.inFlight = this.doFlush()
      .catch(() => undefined)
      .finally(() => {
        this.inFlight = null;
        // Rerun only for work that arrived mid-flush — a failure just recorded
        // must wait for its backoff window, not get hammered immediately.
        const hasPending = [...this.states.values()].some((s) => s.status === "pending");
        if (this.runAgain && hasPending && !this.pausedFor401) {
          this.runAgain = false;
          void this.kick();
        } else {
          this.runAgain = false;
        }
      });
    return this.inFlight;
  }

  /** Resolves when no flush is running (tests + teardown drains). */
  async idle(): Promise<void> {
    while (this.inFlight !== null) await this.inFlight;
  }

  private clearRetry(): void {
    if (this.retryTimer !== null) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
  }

  /** ONE backoff timer after a transient failure (flush.ts scheduleRetry). */
  private scheduleRetry(): void {
    if (this.disposed || this.retryBaseMs <= 0 || this.retryTimer !== null) return;
    const delay = Math.min(this.retryBaseMs * 2 ** this.retryAttempt, UPLOAD_RETRY_MAX_MS);
    this.retryAttempt++;
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      void this.resume(); // failed(retryable) → pending → flush
    }, delay);
  }

  private bumpAttempts(id: string): number {
    const n = (this.attempts.get(id) ?? 0) + 1;
    this.attempts.set(id, n);
    return n;
  }

  private async doFlush(): Promise<void> {
    // Each kick is a fresh permission to try — a 401 pause ends the moment a
    // new trigger fires (post-login focus), exactly like the outbox flusher.
    this.pausedFor401 = false;

    // Snapshot ids (records mutate as stored files leave the queue; work
    // enqueued mid-flush is caught by the kick() rerun).
    const ids = this.records.map((r) => r.upload_id);
    for (const id of ids) {
      const record = this.records.find((r) => r.upload_id === id);
      if (record === undefined) continue;
      const state = this.states.get(id) ?? { status: "pending" as const };
      // PENDING only: failed records re-enter via resume() (backoff timer,
      // online/focus trigger) or their Retry button — never by a bare kick,
      // so a failure recorded a millisecond ago isn't hammered immediately.
      if (state.status !== "pending") continue;

      this.states.set(id, { status: "uploading", progress: 0 });
      this.emit();

      const outcome = await this.send(record, (fraction) => {
        this.states.set(id, { status: "uploading", progress: fraction });
        this.emit();
      });

      if (outcome.kind === "stored") {
        this.retryAttempt = 0; // a POST landed — transient trouble is over
        await this.store.uploadsDelete(id);
        this.records = this.records.filter((r) => r.upload_id !== id);
        this.states.delete(id);
        this.attempts.delete(id);
        this.emit();
        this.deps.onStored?.({ upload_id: id, gig_id: record.gig_id, file_id: outcome.file_id, item_id: outcome.item_id });
        continue;
      }
      if (outcome.kind === "unauthenticated") {
        // Login owns recovery (flush.ts): no timer, records stay durable.
        this.states.set(id, { status: "pending" });
        this.pausedFor401 = true;
        this.clearRetry();
        this.emit();
        return;
      }
      if (outcome.kind === "rejected") {
        // The server said no to THIS file — auto-retrying is noise; the row's
        // Retry button (after e.g. re-exporting a smaller PDF) owns attempt n+1.
        this.states.set(id, { status: "failed", error: outcome.detail, attempts: this.bumpAttempts(id), retryable: false });
        this.emit();
        continue;
      }
      // Transient: the server is unreachable — hammering the REST of the queue
      // now would fail identically; back off once for the whole queue.
      this.states.set(id, { status: "failed", error: outcome.detail, attempts: this.bumpAttempts(id), retryable: true });
      this.scheduleRetry();
      this.emit();
      return;
    }
  }

  private emit(): void {
    for (const l of this.listeners) l();
  }
}

// ------------------------------------------------------------- IDB plumbing

function requestToPromise<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("IndexedDB request failed"));
  });
}

function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onabort = () => reject(tx.error ?? new Error("IndexedDB transaction aborted"));
    tx.onerror = () => reject(tx.error ?? new Error("IndexedDB transaction failed"));
  });
}

/** Versionless open of the shared local DB (src/pass/store.ts pattern —
 * exactly one module, db/idb.ts, ever names a DB version). */
export function openUploadStore(): Promise<UploadStore> {
  return new Promise((resolve, reject) => {
    const open = indexedDB.open(DB_NAME);
    open.onerror = () => reject(open.error ?? new Error("IndexedDB open failed"));
    open.onsuccess = () => {
      const db = open.result;
      db.onversionchange = () => db.close();
      if (!db.objectStoreNames.contains(UPLOADS_STORE)) {
        db.close();
        reject(new Error(`"${UPLOADS_STORE}" store missing — db/idb.ts DB_VERSION bump not wired`));
        return;
      }
      resolve(new IdbUploadStore(db));
    };
  });
}

class IdbUploadStore implements UploadStore {
  constructor(private readonly db: IDBDatabase) {}

  async uploadsPut(record: UploadRecord): Promise<void> {
    const tx = this.db.transaction(UPLOADS_STORE, "readwrite");
    tx.objectStore(UPLOADS_STORE).put(record);
    await txDone(tx); // resolves ONLY on complete — the durability point
  }

  async uploadsList(): Promise<UploadRecord[]> {
    const tx = this.db.transaction(UPLOADS_STORE, "readonly");
    const all = await requestToPromise(tx.objectStore(UPLOADS_STORE).getAll() as IDBRequest<UploadRecord[]>);
    // Enqueue order for the flush loop (queued_at, upload_id as a stable tiebreak).
    return all.sort((a, b) => a.queued_at - b.queued_at || (a.upload_id < b.upload_id ? -1 : 1));
  }

  async uploadsDelete(uploadId: string): Promise<void> {
    const tx = this.db.transaction(UPLOADS_STORE, "readwrite");
    tx.objectStore(UPLOADS_STORE).delete(uploadId);
    await txDone(tx);
  }
}

/** In-memory UploadStore — the test double (idb.ts MemoryStore pattern). */
export class MemoryUploadStore implements UploadStore {
  private uploads = new Map<string, UploadRecord>();

  async uploadsPut(record: UploadRecord): Promise<void> {
    this.uploads.set(record.upload_id, record);
  }

  async uploadsList(): Promise<UploadRecord[]> {
    return [...this.uploads.values()].sort(
      (a, b) => a.queued_at - b.queued_at || (a.upload_id < b.upload_id ? -1 : 1),
    );
  }

  async uploadsDelete(uploadId: string): Promise<void> {
    this.uploads.delete(uploadId);
  }
}

/**
 * Wire the queue's flush triggers (the ARCHITECTURE §5 windows, minus
 * pagehide: a multi-megabyte upload cannot finish inside a keepalive window,
 * and the blob is already durable in IndexedDB — the next foreground resumes
 * it). Returns the teardown.
 */
export function wireUploadTriggers(
  queue: UploadQueue,
  target: {
    addEventListener: typeof window.addEventListener;
    removeEventListener: typeof window.removeEventListener;
  } = window,
  doc: { visibilityState: DocumentVisibilityState } = document,
): () => void {
  // resume(), not kick(): a fresh window is exactly when a transient failure
  // (tunnel, dead venue wifi) deserves an immediate re-attempt.
  const onOnline = () => void queue.resume();
  const onFocus = () => void queue.resume();
  const onVisibility = () => {
    if (doc.visibilityState === "visible") void queue.resume();
  };
  target.addEventListener("online", onOnline);
  target.addEventListener("focus", onFocus);
  target.addEventListener("visibilitychange", onVisibility);
  return () => {
    target.removeEventListener("online", onOnline);
    target.removeEventListener("focus", onFocus);
    target.removeEventListener("visibilitychange", onVisibility);
  };
}
